import {useLazyQuery} from "@apollo/client/react";
import {
    CtrInscriptions,
    CtrMunicipalites,
    CtrQuartiers,
    CtrRegions,
    Maybe,
    Query
} from "../graphql/generated/graphql-main.tsx";
import {gqlTemplates} from "../utils/gql-client.ts";
import {coreActions} from "../store/slice-core";
import {useXDispatch, useXSelector} from "../store/hooks.ts";
import {useEffect, useRef} from "react";
import {findBy} from "../utils/array-utils.ts";
import {useWpContext} from "../components/context/WordPressProvider.tsx";

const gk1: keyof Query = "ctrRegions";
const gk2: keyof Query = "ctrMunicipalites";
const gk3: keyof Query = "ctrQuartiers";

type Options = {
    listings?: CtrInscriptions[];
    deferUntilListings?: boolean;
}

const scopedMunicipalityCodes = (listings: CtrInscriptions[]): string[] => {
    const codes = new Set<string>();
    for (const listing of listings) {
        if (listing.MUN_CODE != null)
            codes.add(listing.MUN_CODE);
    }
    return [...codes];
}

export default function useCtrRegions<
    T extends CtrRegions,
    U extends CtrMunicipalites,
    V extends CtrQuartiers
>(options: Options = {}) {
    const dispatch = useXDispatch();
    const {is_fr} = useWpContext<ZListingSingle>();
    const $regions = useXSelector(s => s.core.REGIONS as T[]);
    const $municipalites = useXSelector(s => s.core.MUNICIPALITES as U[]);
    const $quartiers = useXSelector(s => s.core.QUARTIERS as V[]);
    const $inscriptions = useXSelector(s => s.core.INSCRIPTIONS as CtrInscriptions[]);
    const isFetchingScopedPlaces = useRef(false);
    const scopedListings = options.listings ?? $inscriptions;

    const [fetchRegions, {loading: loading1}] = useLazyQuery<Query>(
        gqlTemplates.regions, {fetchPolicy: "cache-and-network"});
    const [fetchMunicipalites, {loading: loading2}] = useLazyQuery<Query>(
        gqlTemplates.municipalites, {fetchPolicy: "cache-and-network"});
    const [fetchQuartiers, {loading: loading3}] = useLazyQuery<Query>(
        gqlTemplates.quartiers, {fetchPolicy: "cache-and-network"});

    const loadRegions = async () => {
        const {data} = await fetchRegions();
        if (data?.[gk1] == null) return;
        dispatch(coreActions.$setRegions(data?.[gk1] as T[]));
    }
    const loadMunicipalites = async (codes?: string[]) => {
        const {data} = await fetchMunicipalites({variables: {codes}});
        if (data?.[gk2] == null) return;
        dispatch(coreActions.$setMunicipalites(data?.[gk2] as U[]));
    }
    const loadQuartiers = async (munCodes?: string[]) => {
        const {data} = await fetchQuartiers({variables: {munCodes}});
        if (data?.[gk3] == null) return;
        dispatch(coreActions.$setQuartiers(data?.[gk3] as V[]));
    }
    const loadScopedPlaces = async () => {
        // Listings load in one shot today, so this fires once per mount.
        // If incremental batch loading is ever introduced, upgrade isFetchingScopedPlaces
        // to per-code Set tracking to avoid re-fetching codes already in the store.
        if (isFetchingScopedPlaces.current || scopedListings.length == 0) return;
        const codes = scopedMunicipalityCodes(scopedListings);
        if (codes.length == 0) return;
        isFetchingScopedPlaces.current = true;
        await Promise.all([loadMunicipalites(codes), loadQuartiers(codes)]);
    }

    const regByCode = (code?: Maybe<string>): Maybe<T> => {
        return findBy($regions, "CODE", code);
    }
    const munByCode = (code?: Maybe<string>): Maybe<U> => {
        return findBy($municipalites, "CODE", code);
    }
    const qrtByCode = (mun?: Maybe<String>, code?: Maybe<string>): Maybe<V> => {
        return $quartiers.find(it => it.MUN_CODE == mun && it.CODE == code) ?? null;
    }

    const regName = (code?: Maybe<string>, args?: { strict?: boolean }): Maybe<string> => {
        const _key1: keyof T = is_fr ? "CUSTOM_FR" : "CUSTOM_EN";
        const _key2: keyof T = is_fr ? "DESCRIPTION_FRANCAISE" : "DESCRIPTION_ANGLAISE";
        const _reg = regByCode(code);
        if (args?.strict)
            return (_reg?.[_key2] || null) as string | null;
        else
            return (_reg?.[_key1] || _reg?.[_key2] || null) as string | null;
    }
    const regNameByMun = (munCode?: Maybe<string>, args?: { strict?: boolean }): Maybe<string> => {
        const _mun = munByCode(munCode);
        return regName(_mun?.REGION_CODE, args);
    }
    const munName = (code?: Maybe<string>, args?: { strict?: boolean }): Maybe<string> => {
        const _mun = munByCode(code);
        const _key1: keyof U = is_fr ? "CUSTOM_FR" : "CUSTOM_EN";
        const _key2: keyof U = "DESCRIPTION";
        if (args?.strict)
            return (_mun?.[_key2] || null) as string | null;
        else
            return (_mun?.[_key1] || _mun?.[_key2] || null) as string | null;
    }
    const qrtName = (mun?: Maybe<string>, code?: Maybe<string>, args?: { strict?: boolean }): Maybe<string> => {
        const _qrt = qrtByCode(mun ?? null, code ?? null);
        const _key1: keyof V = is_fr ? "CUSTOM_FR" : "CUSTOM_EN";
        const _key2: keyof V = is_fr ? "DESCRIPTION_FRANCAISE" : "DESCRIPTION_ANGLAISE";
        if (args?.strict)
            return (_qrt?.[_key2] || null) as string | null;
        else
            return (_qrt?.[_key1] ?? _qrt?.[_key2] ?? null) as string | null;
    }
    const munsByRegion = (region: Maybe<string>): U[] => {
        return $municipalites.filter(it => it.REGION_CODE == region) ?? [];
    }

    useEffect(() => {
        if ($regions.length == 0) loadRegions();
        if (options.deferUntilListings) {
            loadScopedPlaces();
        } else {
            if ($municipalites.length == 0) loadMunicipalites();
            if ($quartiers.length == 0) loadQuartiers();
        }
    }, [$regions, $municipalites, $quartiers, options.listings, $inscriptions]);

    // True between listings arriving and the scoped Apollo fetch completing.
    // Keeps isLoading true in the parent so the grid never renders with empty municipality labels.
    const scopedLoadingPending = options.deferUntilListings
        && scopedListings.length > 0
        && $municipalites.length == 0;

    return {
        $regions,
        $municipalites,
        $quartiers,
        regByCode,
        munByCode,
        qrtByCode,
        regName,
        regNameByMun,
        munName,
        qrtName,
        munsByRegion,
        loading: loading1 || loading2 || loading3 || scopedLoadingPending
    }
}
